import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Pencil } from 'lucide-react';
import { Button } from './ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

// Individual sortable row
function SortableTableRow({ item, index, onRemove, onEdit }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id || `item-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
    backgroundColor: isDragging ? '#fef3c7' : undefined,
  };

  // Handle both old and new data formats
  const serviceName = item.service || item.description || '—';
  const descriptionText = item.custom_description || (item.service ? item.description : '') || '—';

  return (
    <TableRow ref={setNodeRef} style={style} className={isDragging ? 'shadow-lg' : ''}>
      <TableCell className="w-8">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-200 rounded touch-none"
          title="Drag to reorder"
        >
          <GripVertical className="w-4 h-4 text-slate-400" />
        </button>
      </TableCell>
      <TableCell className="font-medium">{serviceName}</TableCell>
      <TableCell className="text-slate-600">{descriptionText}</TableCell>
      <TableCell className="text-right">{item.quantity}</TableCell>
      <TableCell className="text-right text-xs text-slate-500">
        {item.rate_type === 'hour' ? '/hr' : item.rate_type === 'day' ? '/day' : item.rate_type === 'night' ? '/night' : item.rate_type === 'hole' ? '/hole' : item.rate_type === 'linear_footage' ? '/LF' : 'fixed'}
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">${item.unit_price?.toFixed(2) || '0.00'}</TableCell>
      <TableCell className="text-right font-medium whitespace-nowrap">${item.total?.toFixed(2) || '0.00'}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => onEdit(item, index)} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" title="Edit this line">
            <Pencil className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onRemove(index)} className="text-red-600 hover:text-red-700 hover:bg-red-50" title="Delete this line">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

// Main sortable table component for proposals
export default function SortableProposalItems({ items, onItemsChange, onEdit }) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Ensure all items have IDs
  const itemsWithIds = items.map((item, index) => ({
    ...item,
    id: item.id || `item-${index}-${Date.now()}`
  }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = itemsWithIds.findIndex(item => item.id === active.id);
      const newIndex = itemsWithIds.findIndex(item => item.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = arrayMove(itemsWithIds, oldIndex, newIndex);
        onItemsChange(newItems);
      }
    }
  };

  const handleRemove = (index) => {
    const newItems = itemsWithIds.filter((_, i) => i !== index);
    onItemsChange(newItems);
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead>Service</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right whitespace-nowrap">Qty</TableHead>
              <TableHead className="text-right whitespace-nowrap">Type</TableHead>
              <TableHead className="text-right whitespace-nowrap">Price</TableHead>
              <TableHead className="text-right whitespace-nowrap">Total</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <SortableContext
            items={itemsWithIds.map(item => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <TableBody>
              {itemsWithIds.map((item, index) => (
                <SortableTableRow
                  key={item.id}
                  item={item}
                  index={index}
                  onRemove={handleRemove}
                  onEdit={onEdit}
                />
              ))}
            </TableBody>
          </SortableContext>
        </Table>
        {items.length > 1 && (
          <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
            <GripVertical className="w-3 h-3" /> Drag rows to reorder
          </p>
        )}
      </div>
    </DndContext>
  );
}
